import type { LBNodeData } from '@/proto/logiblocks/logiblocks';
import { AIChannelType, AIAgentType } from '@/proto/logiblocks/logiblocks';
import {
  type GraphData,
  type LBNodeInfo,
  NodeCategory,
  OutputType,
} from '../../types';
import { AICallActionCustomization } from './AICallActionCustomization';
import { defaultNodeData } from '../../graphUtils';

const AIAgentIcon = ({ size }: { size: number }) => (
  <svg
    width={size * 1.8}
    height={size * 1.8}
    viewBox='0 0 44 44'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <rect width='44' height='44' rx='8' fill='var(--danger-thin)' />
    <path
      d='M19.8121 16.5075C19.8765 16.3334 20.1228 16.3334 20.1872 16.5075L21.203 19.2525C21.8107 20.8948 23.1055 22.1896 24.7478 22.7974L27.4928 23.8131C27.6669 23.8775 27.6669 24.1238 27.4928 24.1882L24.7478 25.2039C23.1055 25.8116 21.8107 27.1065 21.203 28.7488L20.1872 31.4937C20.1228 31.6679 19.8765 31.6679 19.8121 31.4937L18.7964 28.7488C18.1887 27.1065 16.8938 25.8116 15.2515 25.2039L12.5066 24.1882C12.3324 24.1238 12.3324 23.8775 12.5066 23.8131L15.2515 22.7974C16.8938 22.1896 18.1887 20.8948 18.7964 19.2525L19.8121 16.5075Z'
      fill='#D1899F'
    />
    <path
      d='M27.8121 12.5075C27.8765 12.3334 28.1228 12.3334 28.1872 12.5075L28.6811 13.8422C28.9343 14.5265 29.4738 15.066 30.1581 15.3192L31.4928 15.8131C31.6669 15.8775 31.6669 16.1238 31.4928 16.1882L30.1581 16.6821C29.4738 16.9353 28.9343 17.4748 28.6811 18.1591L28.1872 19.4937C28.1228 19.6679 27.8765 19.6679 27.8121 19.4937L27.3182 18.1591C27.065 17.4748 26.5255 16.9353 25.8412 16.6821L24.5066 16.1882C24.3324 16.1238 24.3324 15.8775 24.5066 15.8131L25.8412 15.3192C26.5255 15.066 27.065 14.5265 27.3182 13.8422L27.8121 12.5075Z'
      fill='#D1899F'
    />
  </svg>
);

export const AICallActionNodeInfo: LBNodeInfo = {
  protobufNodeDataKey: 'ai_call_action',

  category: NodeCategory.ACTION,

  label: 'AI Agent',

  hasInput: true,

  getIcon: (size: number) => <AIAgentIcon size={size} />,

  outputType: OutputType.SINGLE,

  getSubtitle: (data: LBNodeData, _graph: GraphData) => {
    const aiCallData = data.ai_call_action;
    if (!aiCallData) return undefined;

    const channelLabels: Record<AIChannelType, string> = {
      [AIChannelType.CALL]: 'Call',
      [AIChannelType.EMAIL]: 'Email',
      [AIChannelType.USER_PREFERENCE]: 'User Preference',
      [AIChannelType.UNRECOGNIZED]: 'Unrecognized',
    };

    const channelLabel = channelLabels[aiCallData.channel_type] || 'Call';
    const agentLabel =
      aiCallData.agent_type === AIAgentType.RECOMMENDED
        ? 'Recommended'
        : aiCallData.agent_type === AIAgentType.UNRECOGNIZED
          ? 'Unrecognized'
          : !aiCallData.agent_id
            ? 'Custom'
            : aiCallData.is_inbound_agent
              ? 'Inbound Agent'
              : 'Outbound Agent';

    return `${channelLabel} - ${agentLabel}`;
  },

  getValidationErrors: () => {
    return [];
  },

  CustomizationComponent: AICallActionCustomization,

  createDefaultNodeData: () =>
    defaultNodeData('ai_call_action', {
      channel_type: AIChannelType.CALL,
      agent_type: AIAgentType.RECOMMENDED,
      agent_id: '',
      contact_id: '',
      pca_variable_names: [],
      is_inbound_agent: false,
      collect_shipment_id: false,
      is_transfer_escalation: false,
      reply_wait_seconds: 0,
      dont_wait: false,
    }),

  showCustomizePanelOnCreation: true,
};
